/* eslint-disable camelcase */
import { getReferenceType } from '@modules/apiReference/page/utils/apiReferenceUtils';
import {
  ApiReferenceMemberTypeCollectionHash,
  memberTypeCollectionsByReferenceType,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import { Locale } from '@rbx/intl';
import memoize from 'lodash.memoize';
import {
  APIReference_apiReference as APIReference,
  APIClassReference,
  APIReference_apiReference_collection,
  APIReference_apiReference_ClassReference_callback_parameter,
  APIReference_apiReference_ClassReference_method_return,
  APIReference_apiReference,
  ReferenceType,
} from '@modules/client';
import {
  NextjsSerializableReferenceTypeDataByTypeAlias,
  ReferencePage,
  ReferenceTooltipDataMapByTypeAlias,
  TypeAliasDatumItem,
} from '@modules/apiReference/page/types';
import { parseAliasForCompoundTypes } from '@modules/apiReference/page/sharedUI/utils/linkableTypeReferenceUtils';
import getApiMemberTypeId from '@modules/apiReference/page/utils/apiMemberUtils';
import getReferenceTooltipDataMapKey from './getReferenceTooltipDataMapKey';
import getReferenceTypeFromPrefix from './getReferenceTypeFromPrefix';
import getCleanType from './getCleanType';

export function getReferenceMembersTypeAliases(
  apiReference: APIReference,
  memberTypeCollections: ApiReferenceMemberTypeCollectionHash[],
) {
  const types = new Set<string>();
  const addTypeAliasesFromApiMember = (
    member: // TODO: these types are not sufficient but are fine for now
    (
      | APIReference_apiReference_ClassReference_method_return
      | APIReference_apiReference_ClassReference_callback_parameter
      | APIReference_apiReference_collection
      | Record<string, unknown>
    ) &
      Record<string, unknown>,
  ) => {
    const type = typeof member.type === 'string' ? getCleanType(member.type) : undefined;

    if (type) {
      const { outerType, innerTypes, multipleOrTypes } = parseAliasForCompoundTypes({ type });
      if (outerType && innerTypes) {
        // e.g. 'Array<BundleType>', 'Array<BundleType, BundleType2>'
        types.add(outerType);
        innerTypes.forEach((innerType) => types.add(innerType));
      } else if (multipleOrTypes) {
        multipleOrTypes.forEach((_type) => types.add(_type));
      } else {
        types.add(type);
      }
    }
    const parameters = (member.parameters ??
      []) as (APIReference_apiReference_ClassReference_callback_parameter &
      Record<string, undefined>)[];
    parameters.forEach(addTypeAliasesFromApiMember);
    const returns = (member.returns ??
      []) as (APIReference_apiReference_ClassReference_method_return & Record<string, undefined>)[];
    returns.forEach(addTypeAliasesFromApiMember);
  };
  memberTypeCollections.forEach((memberTypeCollection) => {
    const collection = apiReference[memberTypeCollection as keyof APIReference_apiReference] ?? [];
    (collection as (APIReference_apiReference_collection & Record<string, unknown>)[]).forEach(
      addTypeAliasesFromApiMember,
    );
  });
  return types;
}

// order of reference types to inspect for a matching type alias
const orderedInferredApiReferenceTypes: (
  | ReferenceType.CLASS
  | ReferenceType.DATATYPE
  | ReferenceType.ENUM
  | ReferenceType.LIBRARY
)[] = [ReferenceType.ENUM, ReferenceType.DATATYPE, ReferenceType.CLASS, ReferenceType.LIBRARY];

const explicitMemberTypeIdToReferenceTypeMap = new Map<string, ReferenceType>([
  [
    getApiMemberTypeId({
      referenceType: ReferenceType.CLASS,
      apiName: 'GetTextBoundsParams',
      memberName: 'Font',
    }),
    ReferenceType.DATATYPE,
  ],
  [
    getApiMemberTypeId({
      referenceType: ReferenceType.CLASS,
      apiName: 'BubbleChatMessageProperties',
      memberName: 'Font',
    }),
    ReferenceType.DATATYPE,
  ],
]);

// consistent return type on all api event members
export const eventReturnTypeAlias = 'RBXScriptSignal';

// type aliases that are always available per reference page,
// mapped to the key to fetch by in referenceTooltipDataMapByTypeAlias
const defaultAvailableReferenceTooltipDatumsByAlias: Record<
  string,
  ReturnType<typeof getReferenceTooltipDataMapKey>
> = {
  [eventReturnTypeAlias]: getReferenceTooltipDataMapKey(
    'RBXScriptSignal',
    ReferencePage.ApiReferencePage,
    ReferenceType.DATATYPE,
  ),
};

const getEmptyTypeDatumItem = (typeAliasName: string): TypeAliasDatumItem => ({
  canonicalLabel: typeAliasName,
  // no path, tooltipProps
});

function getReferencePageTypesDataFn(
  referenceTooltipDataMapByTypeAlias: ReferenceTooltipDataMapByTypeAlias,
  apiReference: APIReference,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  locale: Locale,
  // used in memoize fn callback
): Record<string, TypeAliasDatumItem> {
  const result: Map<string, TypeAliasDatumItem> = new Map();
  const notFound: Record<string, string[]> = {};

  Object.keys(defaultAvailableReferenceTooltipDatumsByAlias).forEach((typeAlias) => {
    const datum = referenceTooltipDataMapByTypeAlias.get(
      defaultAvailableReferenceTooltipDatumsByAlias[typeAlias],
    );
    if (!datum)
      throw new Error(
        `Error using defaultAvailableReferenceTooltipDatumsByAlias: no data found for type alias ${typeAlias}`,
      );
    result.set(typeAlias, datum);
  });

  type referenceTooltipDataMapKey = Parameters<typeof referenceTooltipDataMapByTypeAlias.get>[0];
  function addToResultIfFound(
    typeAlias: string, // type alias, as found in the API reference yaml
    typeAliasKey: referenceTooltipDataMapKey,
  ): boolean {
    try {
      const typeDatum = referenceTooltipDataMapByTypeAlias.get(typeAliasKey);
      if (typeDatum) {
        // e.g. 'NullReferencePage.void'
        result.set(typeAlias, typeDatum);
        return true;
      }
      return false;
    } catch (err) {
      throw new Error(`Error: ${err}`);
    }
  }

  const referenceType = getReferenceType(apiReference);
  const unkeyedTypeAliases = getReferenceMembersTypeAliases(
    apiReference,
    memberTypeCollectionsByReferenceType[referenceType],
  );

  /* eslint-disable no-continue */
  // eslint-disable-next-line no-restricted-syntax
  for (const typeAlias of unkeyedTypeAliases) {
    // type may refer to no reference page, e.g. 'void'
    if (
      addToResultIfFound(
        typeAlias,
        getReferenceTooltipDataMapKey(typeAlias, ReferencePage.NullReferencePage),
      )
    )
      continue;

    // type may refer to a Luau reference page
    if (
      addToResultIfFound(
        typeAlias,
        getReferenceTooltipDataMapKey(typeAlias, ReferencePage.LuauReferencePage),
      )
    )
      continue;

    // type may refer to an API reference page
    // with specified reference type (Enum, Class, Datatype)
    const { referenceType: prefixRefType, nonprefixedAliasPart: trimmedTypeAlias } =
      getReferenceTypeFromPrefix(typeAlias);
    if (prefixRefType && trimmedTypeAlias) {
      const aliasKey = getReferenceTooltipDataMapKey(
        trimmedTypeAlias,
        ReferencePage.ApiReferencePage,
        prefixRefType,
        true,
      );
      if (addToResultIfFound(typeAlias, aliasKey)) continue;
    }

    // type may refer to an API reference page
    // with unspecified reference type (Enum, Datatype, Class)
    let found = false;
    const apiMemberTypeId = getApiMemberTypeId({
      referenceType: apiReference.type,
      apiName: apiReference.name,
      memberName: typeAlias,
    });
    const apiReferenceTypesToCheck = explicitMemberTypeIdToReferenceTypeMap.has(apiMemberTypeId)
      ? [explicitMemberTypeIdToReferenceTypeMap.get(apiMemberTypeId)]
      : orderedInferredApiReferenceTypes;
    for (let i = 0; i < apiReferenceTypesToCheck.length; i += 1) {
      const refType = apiReferenceTypesToCheck[i];
      const aliasKey = getReferenceTooltipDataMapKey(
        typeAlias,
        ReferencePage.ApiReferencePage,
        refType,
      );
      // use the first found reference type
      if (addToResultIfFound(typeAlias, aliasKey)) {
        found = true;
        break;
      }
    }

    if (!found) {
      // log warnings for type aliases not found
      notFound[typeAlias] = notFound[typeAlias]
        ? notFound[typeAlias].concat([apiReference.name])
        : [apiReference.name];

      // output empty type alias data
      result.set(typeAlias, getEmptyTypeDatumItem(typeAlias));

      // Do not fail build, but output build warnings (below)
      // ==> TURN OFF FOR NOW
      // throw new Error(
      //   `In getting reference page types for ${apiReference.name}, missing data in referenceTooltipDataMapByTypeAlias for type alias ${typeAlias}.`
      // );
    }
  }

  // if (Object.keys(notFound).length) {
  // log warnings for unhandled type aliases into build log
  // see: https://roblox.atlassian.net/browse/CRK-3812 for caught unhandled types
  // Object.keys(notFound).forEach(typeAlias => {
  //   // eslint-disable-next-line no-console
  //   console.warn(
  //     `MISSING TYPE PAGE: Data for type alias ${typeAlias} not found but is declared as a type in reference pages: ${notFound[
  //       typeAlias
  //     ].join(', ')}`
  //   );
  // });
  // }
  return Object.fromEntries(result);
}

const getReferencePageTypesDataMemoized = memoize(
  getReferencePageTypesDataFn,
  (_, apiReference: APIReference, locale: Locale) => {
    const { type, name } = apiReference;
    if (type && name) {
      return `${locale}:${type}:${name}`;
    }
    throw Error('api reference needs both type and name for the cache key');
  },
);

export default function getReferencePageTypesData(
  referenceTooltipDataMap: ReferenceTooltipDataMapByTypeAlias,
  apiReference: APIReference_apiReference | APIReference,
  // eslint-disable-next-line camelcase
  parents: APIClassReference[] | null,
  locale: Locale,
): NextjsSerializableReferenceTypeDataByTypeAlias {
  let res = getReferencePageTypesDataMemoized(referenceTooltipDataMap, apiReference, locale);
  if (parents)
    parents.forEach((parent) => {
      res = {
        ...res,
        ...getReferencePageTypesDataMemoized(referenceTooltipDataMap, parent, locale),
      };
    });
  return res;
}
